var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { g } from "analytics";
import { Deselect } from "command/basic";
import { commandProcessor } from "command/command";
import { ensureWatermarkLoaded } from "editing/image_export";
import { CloudFiles } from "file/api";
import { FileContext } from "file/context";
import { FileOpenModal } from "file/open_modal";
import { ExportImage2 } from "layout/modals/export_image";
import { LoginModal2 } from "layout/modals/login_modal";
import { MyAccountModal } from "layout/modals/my_account";
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import store, { appStore } from "store/store";
import { TierContext } from "tiers/context";
import Flags from "flags";
export const SharedModalsContext = createContext({
    exportImage: () => { },
    openMyAccount: () => { },
    openFiles: () => { },
    saveToCloud: () => { }
});
export const SharedModalsProvider = ({ auth, children }) => {
    const [isExportOpen, setIsExportOpen] = useState(false);
    const [imageExportOutput, setImageExportOutput] = useState();
    const fileContext = useContext(FileContext);
    const [isMyAccountOpen, setIsMyAccountOpen] = useState(false);
    const [isFilesOpen, setIsFilesOpen] = useState(false);
    const { tier, allow } = useContext(TierContext);
    const isBoost = allow("boost");
    const [includeWatermark, setIncludeWatermark] = useState(Flags.isExportWatermarkEnabled && !isBoost);
    // This is required so latest auth can be accessed from the saveToCloud callback.
    const authRef = useRef(auth);
    useEffect(() => {
        authRef.current = auth;
    }, [auth]);
    useEffect(() => {
        setIncludeWatermark(Flags.isExportWatermarkEnabled && !allow("boost"));
    }, [tier]);
    const runExport = (watermark) => {
        setImageExportOutput(undefined);
        // Deselect any selected items. Do this first as this
        // will trigger a React update, setting the zoom.
        let deselectCommand = new Deselect(store.selectedObjectsID());
        deselectCommand.execute();
        // Execute on background thread.
        setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
            const shouldWatermark = Flags.isExportWatermarkEnabled && watermark;
            if (shouldWatermark) {
                yield ensureWatermarkLoaded();
            }
            // This will potentially take a long time.
            let imageExportOutput = fileContext.exportImages({ watermark: shouldWatermark });
            setImageExportOutput(imageExportOutput);
            // Revert deselection.
            deselectCommand.undo();
        }), 100);
    };
    const saveImage = (source) => {
        if (store.items().length + store.arrows().length == 0) {
            toastr.error("Image is empty. Add some items before saving");
            return;
        }
        g('ExportToImage', source);
        /** Log some stats about the image. */
        const state = appStore.getState();
        g('ExportedImageStats::strokeThickness', state.theme.strokeThickness);
        if (state.renderArea) {
            g('ExportedImageStats::area');
        }
        const initialWatermark = Flags.isExportWatermarkEnabled && !allow("boost");
        setIncludeWatermark(initialWatermark);
        setIsExportOpen(true);
        runExport(initialWatermark);
    };
    const handleToggleWatermark = (checked) => {
        setIncludeWatermark(checked);
        runExport(checked);
    };
    const contextValue = useMemo(() => ({
        exportImage: (source) => saveImage(source),
        openMyAccount: () => setIsMyAccountOpen(true),
        openFiles: () => setIsFilesOpen(true),
        saveToCloud: (opts) => {
            var _a;
            const auth = authRef.current;
            if (auth.state !== "logged_in")
                return;
            if (store.items().length == 0 && store.arrows().length == 0) {
                toastr.warning("Cannot save an empty diagram");
                return;
            }
            // Extract thumbnail and current document data.
            const thumbnail = fileContext.exportThumbnail();
            CloudFiles.save(auth, thumbnail, (_a = opts === null || opts === void 0 ? void 0 : opts.forceNew) !== null && _a !== void 0 ? _a : false, {
                onDiagramsLimitExceeded: () => setIsFilesOpen(true)
            });
            commandProcessor.markClean();
        }
    }), [authRef, fileContext, setIsFilesOpen]);
    return React.createElement(SharedModalsContext.Provider, { value: contextValue },
        React.createElement(ExportImage2, { isOpen: isExportOpen, setIsOpen: setIsExportOpen, exportedImages: imageExportOutput, includeWatermark: includeWatermark, onToggleWatermark: handleToggleWatermark }),
        React.createElement(MyAccountModal, { auth: auth, isOpen: isMyAccountOpen, setIsOpen: setIsMyAccountOpen }),
        React.createElement(FileOpenModal, { auth: auth, isOpen: isFilesOpen, setIsOpen: setIsFilesOpen }),
        children);
};
export const LoginModalContext = createContext({
    openLogin: () => { },
});
export const LoginModalProvider = ({ auth, children }) => {
    const [isLoginOpen, setIsLoginOpen] = useState(false);
    const [successCallback, setSuccessCallback] = useState();
    const contextValue = useMemo(() => ({
        openLogin: (callback) => {
            // Need to set as a function that returns the actual callback.
            setSuccessCallback(() => callback);
            setIsLoginOpen(true);
        },
    }), [setSuccessCallback, setIsLoginOpen]);
    return React.createElement(LoginModalContext.Provider, { value: contextValue },
        React.createElement(LoginModal2, { auth: auth, isOpen: isLoginOpen, setIsOpen: setIsLoginOpen, onLoginSuccessCallback: successCallback }),
        children);
};
// Exports
export const useSharedModals = () => useContext(SharedModalsContext);
export const useLoginModal = () => useContext(LoginModalContext);
